import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { UserPlus, Users, Mail, History, Trash2, Copy, CheckCircle2, AlertCircle, Shield, Eye, Clock, RefreshCw, X, LayoutDashboard, Briefcase } from "lucide-react";
import UserDashboardAccessModal from "../components/UserDashboardAccessModal";

const fmtDate = (s) => s ? new Date(s).toLocaleString("es-DO", { dateStyle: "medium", timeStyle: "short" }) : "—";
const fmtRelative = (s) => {
  if (!s) return "—";
  const ms = Date.now() - new Date(s).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "ahora";
  if (m < 60) return `hace ${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h}h`;
  return `hace ${Math.floor(h / 24)}d`;
};

const ROLE_META = {
  admin:         { label: "Admin",             color: "#dc2626", bg: "#fee2e2", icon: Shield,    desc: "Acceso completo: gestionar usuarios, subir CSV, configurar webhooks." },
  sales_manager: { label: "Gerente de Ventas", color: "#2563eb", bg: "#dbeafe", icon: Briefcase, desc: "Solo lectura · Sales Performance + CRM Overview (incluye Trade-In Seminuevos). Sin acceso al resto." },
  viewer:        { label: "Viewer",            color: "#16a34a", bg: "#dcfce7", icon: Eye,       desc: "Solo lectura: ver dashboards y datos. No puede modificar configuración." },
};

export default function AdminUsersPage() {
  const { user: currentUser } = useAuth();
  const [tab, setTab] = useState("users");
  if (currentUser?.role !== "admin") {
    return (
      <div className="surface p-8 text-center" data-testid="admin-users-forbidden">
        <AlertCircle size={32} className="mx-auto mb-3 text-gray-400"/>
        <h2 className="font-display text-xl font-extrabold mb-2">Acceso restringido</h2>
        <p className="text-sm text-gray-600">Esta sección es exclusiva para administradores.</p>
      </div>
    );
  }
  return (
    <div data-testid="admin-users-page">
      <header className="flex justify-between items-start mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold" style={{ letterSpacing: "-0.02em" }}>Usuarios y Roles</h1>
          <p className="text-sm text-gray-500 font-semibold mt-1">
            Gestiona quién puede acceder al dashboard y con qué nivel de permisos.
          </p>
        </div>
      </header>

      <div className="lp2-tabs mb-5" role="tablist">
        <button role="tab" aria-selected={tab === "users"} className={`lp2-tab ${tab === "users" ? "is-active" : ""}`} onClick={() => setTab("users")} data-testid="tab-users">
          <Users size={14}/> Usuarios
        </button>
        <button role="tab" aria-selected={tab === "invites"} className={`lp2-tab ${tab === "invites" ? "is-active" : ""}`} onClick={() => setTab("invites")} data-testid="tab-invites">
          <Mail size={14}/> Invitaciones
        </button>
        <button role="tab" aria-selected={tab === "audit"} className={`lp2-tab ${tab === "audit" ? "is-active" : ""}`} onClick={() => setTab("audit")} data-testid="tab-audit">
          <History size={14}/> Audit Log
        </button>
      </div>

      {tab === "users"   && <UsersPanel  currentUser={currentUser}/>}
      {tab === "invites" && <InvitesPanel/>}
      {tab === "audit"   && <AuditPanel/>}
    </div>
  );
}

function UsersPanel({ currentUser }) {
  const [users, setUsers] = useState(null);
  const [busy, setBusy] = useState(null); // user_id being modified
  const [msg, setMsg] = useState(null);
  const [accessModal, setAccessModal] = useState(null);  // { userId, email } | null

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data.users)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);

  const changeRole = async (u, newRole) => {
    if (u.role === newRole) return;
    setBusy(u.id);
    setMsg(null);
    try {
      await api.patch(`/admin/users/${u.id}`, { role: newRole });
      setMsg({ ok: true, text: `Rol de ${u.email} → ${newRole}` });
      load();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Error al actualizar rol" });
    } finally { setBusy(null); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`¿Eliminar usuario ${u.email}? Esta acción no se puede deshacer.`)) return;
    setBusy(u.id);
    setMsg(null);
    try {
      await api.delete(`/admin/users/${u.id}`);
      setMsg({ ok: true, text: `Usuario ${u.email} eliminado` });
      load();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Error al eliminar usuario" });
    } finally { setBusy(null); }
  };

  if (users === null) return <Spinner/>;

  return (
    <div className="space-y-4">
      {msg && <Banner ok={msg.ok}>{msg.text}</Banner>}

      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
        {Object.entries(ROLE_META).map(([key, meta]) => {
          const Icon = meta.icon;
          const count = users.filter((u) => u.role === key).length;
          return (
            <div key={key} className="surface p-4" style={{ borderLeft: `3px solid ${meta.color}` }}>
              <div className="flex items-center gap-3">
                <div className="kpi-icon-clean" style={{ width: 36, height: 36, color: meta.color, background: meta.bg }}>
                  <Icon size={16}/>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-display font-extrabold">{meta.label}</h3>
                    <span className="pill" style={{ background: meta.bg, color: meta.color, fontSize: 11 }}>{count}</span>
                  </div>
                  <p className="text-xs text-gray-500 font-semibold mt-0.5">{meta.desc}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="surface p-0 overflow-hidden">
        <div className="flex items-center justify-between p-4" style={{ borderBottom: "1px solid #f1f5f9" }}>
          <h2 className="font-display font-extrabold">Cuentas activas · {users.length}</h2>
          <button className="btn flex items-center gap-2" onClick={load} data-testid="users-refresh"><RefreshCw size={14}/> Refrescar</button>
        </div>
        <table className="w-full" data-testid="users-table">
          <thead>
            <tr style={{ borderBottom: "1px solid #f1f5f9", background: "#fafbfc" }}>
              <th className="text-left text-xs font-extrabold text-gray-500 px-4 py-2.5 uppercase tracking-wider">Usuario</th>
              <th className="text-left text-xs font-extrabold text-gray-500 px-4 py-2.5 uppercase tracking-wider">Rol</th>
              <th className="text-left text-xs font-extrabold text-gray-500 px-4 py-2.5 uppercase tracking-wider">Creado</th>
              <th className="text-right text-xs font-extrabold text-gray-500 px-4 py-2.5 uppercase tracking-wider">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const meta = ROLE_META[u.role] || ROLE_META.viewer;
              const isSelf = u.id === currentUser.id;
              return (
                <tr key={u.id} style={{ borderBottom: "1px solid #f1f5f9" }} data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="avatar" style={{ width: 32, height: 32, fontSize: 12 }}>{(u.name || u.email).slice(0,1).toUpperCase()}</div>
                      <div>
                        <div className="font-display font-extrabold text-sm">{u.name || "—"} {isSelf && <span className="pill ml-1" style={{ fontSize: 10, padding: "1px 8px" }}>tú</span>}</div>
                        <div className="text-xs text-gray-500">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      className="select-input"
                      style={{ fontSize: 12, padding: "4px 8px", minWidth: 110 }}
                      value={u.role}
                      onChange={(e) => changeRole(u, e.target.value)}
                      disabled={busy === u.id || isSelf}
                      data-testid={`user-role-${u.email}`}
                    >
                      {Object.entries(ROLE_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2 flex-wrap">
                      <button
                        className="btn flex items-center gap-1.5"
                        style={{ fontSize: 12, padding: "4px 10px", color: "#0f172a" }}
                        onClick={() => setAccessModal({ userId: u.id, email: u.email })}
                        disabled={busy === u.id}
                        data-testid={`user-dashboards-${u.email}`}
                        title="Configurar qué dashboards ve este usuario"
                      >
                        <LayoutDashboard size={12}/> Dashboards
                      </button>
                      <button
                        className="btn flex items-center gap-1.5"
                        style={{ fontSize: 12, padding: "4px 10px", color: "#dc2626" }}
                        onClick={() => removeUser(u)}
                        disabled={busy === u.id || isSelf}
                        data-testid={`user-delete-${u.email}`}
                        title={isSelf ? "No puedes eliminarte" : "Eliminar usuario"}
                      >
                        <Trash2 size={12}/> Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <UserDashboardAccessModal
        open={!!accessModal}
        onClose={() => setAccessModal(null)}
        userId={accessModal?.userId}
        userEmail={accessModal?.email}
      />
    </div>
  );
}

function InvitesPanel() {
  const [invites, setInvites] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", role: "viewer" });
  const [lastLink, setLastLink] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = () => api.get("/admin/invites")
    .then((r) => {
      // Rebuild magic_link on the client using the browser's actual origin.
      // The backend cannot reliably know the public domain because the K8s
      // ingress rewrites the Origin header. See routes/admin_users.py.
      const invs = (r.data.invites || []).map((inv) => ({
        ...inv,
        magic_link: `${window.location.origin}/accept-invite/${inv.token}`,
      }));
      setInvites(invs);
    })
    .catch(() => setInvites([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setCreating(true);
    setMsg(null);
    try {
      const r = await api.post("/admin/invites", { email: form.email.trim().toLowerCase(), role: form.role });
      // Build the magic link using the browser's current origin (see load()).
      const link = `${window.location.origin}/accept-invite/${r.data.token}`;
      setLastLink({ link, email: form.email, role: form.role, expires_at: r.data.expires_at });
      setForm({ email: "", role: "viewer" });
      load();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Error al crear invitación" });
    } finally { setCreating(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("¿Revocar esta invitación? El link dejará de funcionar.")) return;
    try { await api.delete(`/admin/invites/${id}`); load(); }
    catch (e) { setMsg({ ok: false, text: e.response?.data?.detail || "Error al revocar" }); }
  };

  if (invites === null) return <Spinner/>;

  return (
    <div className="space-y-4">
      {msg && <Banner ok={msg.ok}>{msg.text}</Banner>}

      <div className="surface p-5">
        <h2 className="font-display font-extrabold mb-1 flex items-center gap-2"><UserPlus size={16}/> Crear nueva invitación</h2>
        <p className="text-xs text-gray-500 font-semibold mb-4">
          Genera un magic link único. Compártelo por WhatsApp, Slack o email manualmente — el destinatario podrá crear su cuenta y entrar directamente.
        </p>
        <form onSubmit={submit} className="grid gap-3" style={{ gridTemplateColumns: "1.5fr 1fr auto" }}>
          <input
            type="email"
            className="text-input"
            placeholder="email@empresa.com"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            required
            data-testid="invite-email-input"
          />
          <select
            className="select-input"
            value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
            data-testid="invite-role-select"
          >
            {Object.entries(ROLE_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
          </select>
          <button type="submit" className="btn primary flex items-center gap-2" disabled={creating || !form.email} data-testid="invite-create-btn">
            {creating ? <span className="spinner"/> : <UserPlus size={14}/>}
            Generar Link
          </button>
        </form>
      </div>

      {lastLink && (
        <div className="surface p-5" style={{ background: "#fffbeb", borderColor: "#fde68a" }} data-testid="invite-result-card">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="font-display font-extrabold flex items-center gap-2"><CheckCircle2 size={16} style={{ color: "#16a34a" }}/> Invitación creada</h3>
              <p className="text-xs text-gray-700 font-semibold mt-1">
                Para <strong>{lastLink.email}</strong> · rol <strong>{ROLE_META[lastLink.role]?.label}</strong> · expira <strong>{fmtDate(lastLink.expires_at)}</strong>
              </p>
            </div>
            <button className="btn" style={{ padding: 6 }} onClick={() => setLastLink(null)} aria-label="Cerrar"><X size={14}/></button>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={lastLink.link}
              readOnly
              className="text-input flex-1"
              style={{ fontFamily: "'Outfit', monospace", fontSize: 12, background: "#fff" }}
              data-testid="invite-magic-link"
              onFocus={(e) => e.target.select()}
            />
            <CopyBtn value={lastLink.link}/>
          </div>
          <p className="text-xs text-gray-600 mt-2">
            🔒 Comparte este link sólo con la persona invitada. Una vez usado, el link se invalida automáticamente.
          </p>
        </div>
      )}

      <div className="surface p-0 overflow-hidden">
        <div className="flex items-center justify-between p-4" style={{ borderBottom: "1px solid #f1f5f9" }}>
          <h2 className="font-display font-extrabold">Historial de invitaciones · {invites.length}</h2>
          <button className="btn flex items-center gap-2" onClick={load}><RefreshCw size={14}/> Refrescar</button>
        </div>
        {invites.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">Aún no has creado invitaciones.</div>
        ) : (
          <table className="w-full" data-testid="invites-table">
            <thead>
              <tr style={{ borderBottom: "1px solid #f1f5f9", background: "#fafbfc" }}>
                <th className="text-left text-xs font-extrabold text-gray-500 px-4 py-2.5 uppercase tracking-wider">Email</th>
                <th className="text-left text-xs font-extrabold text-gray-500 px-4 py-2.5 uppercase tracking-wider">Rol</th>
                <th className="text-left text-xs font-extrabold text-gray-500 px-4 py-2.5 uppercase tracking-wider">Estado</th>
                <th className="text-left text-xs font-extrabold text-gray-500 px-4 py-2.5 uppercase tracking-wider">Creada</th>
                <th className="text-left text-xs font-extrabold text-gray-500 px-4 py-2.5 uppercase tracking-wider">Por</th>
                <th className="text-right text-xs font-extrabold text-gray-500 px-4 py-2.5 uppercase tracking-wider">Acción</th>
              </tr>
            </thead>
            <tbody>
              {invites.map((inv) => (
                <tr key={inv.id} style={{ borderBottom: "1px solid #f1f5f9" }} data-testid={`invite-row-${inv.email}`}>
                  <td className="px-4 py-3 text-sm">{inv.email}</td>
                  <td className="px-4 py-3">
                    <span className="pill" style={{ background: ROLE_META[inv.role]?.bg, color: ROLE_META[inv.role]?.color, fontSize: 11 }}>
                      {ROLE_META[inv.role]?.label || inv.role}
                    </span>
                  </td>
                  <td className="px-4 py-3"><InviteStatus s={inv.status}/></td>
                  <td className="px-4 py-3 text-xs text-gray-600">{fmtRelative(inv.created_at)}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{inv.created_by}</td>
                  <td className="px-4 py-3 text-right">
                    {inv.status === "pending" ? (
                      <div className="flex items-center justify-end gap-2">
                        <CopyBtn value={inv.magic_link} compact/>
                        <button className="btn" style={{ fontSize: 12, padding: "4px 10px", color: "#dc2626" }} onClick={() => revoke(inv.id)} data-testid={`invite-revoke-${inv.email}`}>
                          Revocar
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function InviteStatus({ s }) {
  const meta = {
    pending: { label: "Pendiente", bg: "#fef3c7", fg: "#92400e" },
    used:    { label: "Usado",     bg: "#dcfce7", fg: "#166534" },
    revoked: { label: "Revocada",  bg: "#f3f4f6", fg: "#6b7280" },
    expired: { label: "Expirada",  bg: "#fee2e2", fg: "#991b1b" },
  }[s] || { label: s, bg: "#f3f4f6", fg: "#6b7280" };
  return <span className="pill" style={{ background: meta.bg, color: meta.fg, fontSize: 11 }}>{meta.label}</span>;
}

const ACTION_META = {
  "auth.login":   { label: "Inició sesión",    color: "#16a34a" },
  "auth.logout":  { label: "Cerró sesión",     color: "#6b7280" },
  "invite.created":  { label: "Creó invitación",  color: "#3b82f6" },
  "invite.accepted": { label: "Aceptó invitación", color: "#16a34a" },
  "invite.revoked":  { label: "Revocó invitación", color: "#f97316" },
  "user.role_changed": { label: "Cambió rol",     color: "#a855f7" },
  "user.deleted":      { label: "Eliminó usuario", color: "#dc2626" },
  "data.csv_uploaded": { label: "Subió CSV",      color: "#3b82f6" },
  "integration.zapier_token_updated": { label: "Cambió token Zapier", color: "#f97316" },
};

function AuditPanel() {
  const [events, setEvents] = useState(null);
  const [filter, setFilter] = useState("");

  const load = () => {
    const params = filter ? { action: filter } : {};
    api.get("/admin/audit-log", { params }).then((r) => setEvents(r.data.events)).catch(() => setEvents([]));
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  if (events === null) return <Spinner/>;

  return (
    <div className="space-y-4">
      <div className="surface p-0 overflow-hidden">
        <div className="flex items-center justify-between p-4 flex-wrap gap-3" style={{ borderBottom: "1px solid #f1f5f9" }}>
          <div>
            <h2 className="font-display font-extrabold flex items-center gap-2"><History size={16}/> Registro de Auditoría · {events.length}</h2>
            <p className="text-xs text-gray-500 font-semibold mt-0.5">Quién hizo qué y cuándo. Útil para investigar cambios o accesos.</p>
          </div>
          <div className="flex items-center gap-2">
            <select className="select-input" style={{ fontSize: 12, padding: "4px 8px" }} value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="audit-filter">
              <option value="">Todas las acciones</option>
              {Object.entries(ACTION_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
            </select>
            <button className="btn flex items-center gap-2" onClick={load} data-testid="audit-refresh"><RefreshCw size={14}/></button>
          </div>
        </div>
        {events.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">Sin eventos registrados.</div>
        ) : (
          <ul className="divide-y" style={{ borderColor: "#f1f5f9" }} data-testid="audit-list">
            {events.map((e) => {
              const meta = ACTION_META[e.action] || { label: e.action, color: "#6b7280" };
              return (
                <li key={e.id} className="px-4 py-3 flex items-start gap-3 hover:bg-gray-50">
                  <div className="rounded-full mt-1.5" style={{ width: 8, height: 8, background: meta.color, flexShrink: 0 }}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      <strong className="font-display">{e.actor_email || "—"}</strong>
                      <span className="text-gray-600"> · {meta.label}</span>
                      {e.target && e.target !== e.actor_email && <span className="text-gray-900 font-semibold"> · {e.target}</span>}
                    </div>
                    {e.metadata && Object.keys(e.metadata).length > 0 && (
                      <div className="text-xs text-gray-500 mt-1 font-mono">
                        {Object.entries(e.metadata).map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ")}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 flex-shrink-0" title={fmtDate(e.created_at)}>{fmtRelative(e.created_at)}</div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function CopyBtn({ value, compact = false }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button type="button" className="btn flex items-center gap-1.5" style={{ fontSize: compact ? 12 : 13, padding: compact ? "4px 10px" : "6px 12px" }} onClick={copy} disabled={!value}>
      {copied ? <><CheckCircle2 size={12}/> Copiado</> : <><Copy size={12}/> Copiar</>}
    </button>
  );
}

function Banner({ ok, children }) {
  return (
    <div className={`pill ${ok ? "good" : "bad"} flex items-center gap-2`} style={{ padding: "8px 14px", fontSize: 13 }}>
      {ok ? <CheckCircle2 size={14}/> : <AlertCircle size={14}/>} {children}
    </div>
  );
}

function Spinner() {
  return <div className="surface p-12 grid place-items-center"><div className="spinner" style={{ borderColor: "#11182733", borderTopColor: "#111827" }}/></div>;
}
